import { EngineReportDetails, PerformanceParametersVm } from '@navstation-portal/reporting/data-model/engine-report';
import { Units } from '@navstation-portal/shared/data-model/ui';
import { PrimeMoverTypes, VesselSpec } from '@navstation-portal/vessels/data-model/vessel';
import { RangeLimits, TooltipSeverity, getRangeTooltip } from '../range-tooltip.utils';

const SIX_HOURS = 6;
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

export function buildPerformanceParametersVm(
  details: EngineReportDetails | undefined,
  vesselSpec: VesselSpec | undefined,
  reportNumber: number | undefined
): PerformanceParametersVm {
  const meReportNumber = reportNumber?.toString() ?? '1';
  const reportFrom = details?.reportFrom;
  const reportTo = details?.reportTo;

  const reportPeriod = (() => {
    if (!reportFrom || !reportTo) return 0;
    const hours = (reportTo.getTime() - reportFrom.getTime()) / 3_600_000;
    return Math.max(Math.round(hours * 100) / 100, 0);
  })();

  const maxElectricalPower = vesselSpec?.engine?.max_electrical_power;
  const effectivePower = details?.effectivePower;
  const loadPercentMcr = !maxElectricalPower ? 100 : ((effectivePower ?? 0) * 100) / maxElectricalPower;

  const kwhStart = details?.kwhStart;
  const kwhEnd = details?.kwhEnd;
  const avgPower = reportPeriod ? ((kwhEnd ?? 0) - (kwhStart ?? 0)) / reportPeriod : undefined;

  const isDieselElectricPropulsion =
    vesselSpec?.information?.prime_mover_type === PrimeMoverTypes.DIESEL_ELECTRIC_PROPULSION;
  const hasTorsionmeter = vesselSpec?.information?.has_torsionmeter ?? false;

  const showSingleEngineRunning = isDieselElectricPropulsion
    ? (vesselSpec?.prime_mover?.propulsion_motors?.length ?? 0) > 1
    : (vesselSpec?.engine?.me_engines?.length ?? 0) > 1;

  const indicatedPower = details?.indicatedPower;
  const effectivePowerOutOfRange =
    indicatedPower != null &&
    effectivePower != null &&
    (effectivePower < indicatedPower * 0.85 || effectivePower > indicatedPower);

  const effectivePowerRange: RangeLimits = { min: (indicatedPower ?? 0) * 0.85, max: indicatedPower ?? 0 };
  const effectivePowerTooltip = getRangeTooltip(
    'Effective Power',
    effectivePowerRange,
    Units.kilowatts,
    TooltipSeverity.Error
  );

  const shaftGeneratorPower = details?.shaftGeneratorPower;
  const shaftGeneratorPowerNegative = (shaftGeneratorPower ?? 0) < 0;

  const reportToTooltip = (() => {
    if (reportFrom && reportTo && reportTo < reportFrom) {
      return 'Please enter a report end date and time that is after the report start time';
    }
    return undefined;
  })();

  const reportPeriodWarning = !reportToTooltip && reportPeriod > SIX_HOURS;
  const reportPeriodWarningTooltip = 'Report period should be less than 6 hours';

  const reportMoreThanOneWeekOld = !!reportTo && reportTo.getTime() < Date.now() - ONE_WEEK_MS;

  return {
    meReportNumber,
    reportPeriod,
    loadPercentMcr,
    avgPower,
    isDieselElectricPropulsion,
    hasTorsionmeter,
    showSingleEngineRunning,
    engineRpmLabel: isDieselElectricPropulsion ? 'Motor RPM' : 'Engine RPM',
    singleEngineRunningLabel: isDieselElectricPropulsion ? 'Single Motor Running' : 'Single Engine Running',
    effectivePowerOutOfRange,
    effectivePowerTooltip,
    shaftGeneratorPowerNegative,
    reportToTooltip,
    reportPeriodWarning,
    reportPeriodWarningTooltip,
    reportMoreThanOneWeekOld,
  };
}
